import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Smartphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { changeLanguage } from "../../features/localization/localizationSlice";
import { portalSignOut, selectPortalSession } from "../../features/portal/portal-slice";
import { clearAuthenticationData } from "../../features/authentication/authenticationSlice";
import { signOutEverywhere } from "../../features/authentication/signOutEverywhere";
import { useTheme } from "../../features/theme/useTheme";
import { ROUTES } from "../../routes";
import TimeplannedLogo from "../auth/TimeplannedLogo";
import { rememberPortalLanguageChoice } from "../../features/portal/utils/portalLanguage";
import LanguageMenu from "./LanguageMenu";
import PortalNav from "./PortalNav";
import PortalContextSwitcher from "./PortalContextSwitcher";
import GetTheAppDialog from "../app-stores/GetTheAppDialog";

/**
 * Minimal chrome for the member portal — deliberately free of the instructor
 * app's navigation, search and subscription banner. Signed in, it adds the
 * portal's own sections (Next, Money, Library, Progress, Messages, Me).
 */
const PortalHeader = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useAppSelector(selectPortalSession);
  const [getAppOpen, setGetAppOpen] = useState(false);

  // The dark-mode class is normally applied by the instructor chrome; the portal
  // has its own chrome, so it must apply the theme (incl. OS auto) itself.
  useTheme();

  // Signing out of the portal signs the person out of every context on this device.
  const handleSignOut = async () => {
    await signOutEverywhere();
    dispatch(portalSignOut());
    dispatch(clearAuthenticationData());
    navigate(ROUTES.PORTAL_LOGIN);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-grey-70 bg-white dark:border-grey-50 dark:bg-primary-10">
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
        <TimeplannedLogo className="h-6 fill-grey-20 dark:fill-white" />
        <div className="flex items-center gap-2">
          <LanguageMenu
            value={i18n.language.slice(0, 2)}
            onSelect={(code) => {
              // An explicit pick wins over the member's own language on every later visit.
              rememberPortalLanguageChoice(code);
              dispatch(changeLanguage(code));
            }}
          />
          <PortalContextSwitcher />
          {session && (
            <button
              type="button"
              className="flex min-h-[44px] items-center rounded-md px-2 text-grey-20 hover:bg-grey-95 dark:text-white dark:hover:bg-primary-20"
              aria-label={t("getTheApp.title")}
              title={t("getTheApp.title")}
              onClick={() => setGetAppOpen(true)}
            >
              <Smartphone aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
          {session && (
            <button type="button" className="whitespace-nowrap text-sm text-primary-40 hover:underline dark:text-primary-90" onClick={handleSignOut}>
              {t("portal.signOut", "Sign out")}
            </button>
          )}
        </div>
      </div>
      {session && <PortalNav />}
      {getAppOpen && <GetTheAppDialog audience="member" onClose={() => setGetAppOpen(false)} />}
    </header>
  );
};

export default PortalHeader;
